/**
 * MAS Direct template core hook. Encapsulates all state, loaders, handlers, and effects for MAS Direct template.
 */
import React from 'react';
import { useAutoSave } from 'hooks/useAutoSave';
import { useStore } from 'hooks/useStore';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  applyDateFilter,
  checkSectionCompletion,
  formatDateForAPI,
  formatDateForChartAPI,
  getDefaultViewDate,
  getMarketTrendsChartData,
  preserveGlobalFields as preserveGlobalFieldsLogic,
  transformMarketShareData,
} from '../../bidAnalysisTemplate.logic';
import { validateField as validateFieldUtil } from '../../shared/utils/fieldValidation';
import classes from '../../bidAnalysisTemplate.module.scss';
import '../../chartConfig';
import { useBidAnalysisState } from '../../hooks/useBidAnalysisState';
import { useBidDetailsLoad } from '../../hooks/useBidDetailsLoad';
import { useRecalculation } from '../../hooks/useRecalculation';
import { useSaveAndSubmit } from '../../hooks/useSaveAndSubmit';
import { useTemplateNavigation } from '../../hooks/useTemplateNavigation';
import { loadBidDetailsImpl } from '../../full/loadBidDetailsImpl';
import { useDataLoaders } from '../../hooks/useDataLoaders';
import { useSaveBid } from '../../hooks/useSaveBid';
import { useFieldHandlers } from '../../hooks/useFieldHandlers';
import { useBidHandlers } from '../../hooks/useBidHandlers';
import { useTemplateComputed } from '../../hooks/useTemplateComputed';
import { useMASDirectEffects } from './useMASDirectEffects';
import { useMarketTrendsChartOptions } from '../../hooks/useMarketTrendsChartOptions';
import { useCommonEffects } from '../../hooks/useCommonEffects';
import { AUTO_SAVE_ENABLED } from '../../constants/templateConstants';
import { SECTION_DISPLAY_NAMES, SECTION_NAMES } from '../../constants/sectionNames';
import type { MASTemplateContentCtx } from '../../components/content/MASTemplateContent';

export interface UseMASDirectTemplateCoreResult {
  loading: boolean;
  bidDetails: any;
  templateType: string;
  productType: string | null;
  templateData: any;
  masCtx: MASTemplateContentCtx;
  layoutProps: {
    pendingNavigation: string | null;
    setPendingNavigation: (path: string | null) => void;
    isBackNavigation: boolean;
    setIsBackNavigation: (v: boolean) => void;
    setIsNavigatingAway: (v: boolean) => void;
    isSaving: boolean;
    navigate: (path: string) => void;
    getBackNavigationPath: () => string;
    handleSaveBid: (isAutoSave?: boolean) => Promise<true | undefined>;
    productName: string;
    isSubmittingForReview: boolean;
    progress: number;
    selectedStatus: string;
    bidStatus: string;
    onSave: () => void | Promise<true | undefined>;
    onSubmit: () => Promise<void>;
    onStatusChange: (status: string) => void;
    isManagerReviewMode: boolean;
    onRequestModification: () => void;
    onApprove: () => void;
    missingFieldsReport: any;
    onShowMissingFields: () => void;
    templateType: string;
    onRequestNavigation: (path: string) => void;
    sidebarCollapsed: boolean;
    onToggleCollapse: () => void;
    navigationItems: any[];
    activeSection: string;
    onSectionClick: (sectionId: string) => void;
    productType: 'direct' | 'indirect' | null;
  };
  notesPanelProps: {
    ref: React.RefObject<any>;
    bidId: number;
    sectionName: string;
    notes: any[];
    onNotesChange: () => void;
    currentUser: any;
    readOnly: boolean;
  };
  chartModalProps: {
    visible: boolean;
    onHide: () => void;
    selectedCompany: string | null;
    tempDateRange: any;
    calendarViewDates: any;
    getDefaultViewDate: (isStart?: boolean) => Date;
    handleTempDateRangeChange: (start: Date | null, end: Date | null) => void;
    setCalendarViewDates: (dates: any) => void;
    handleApplyDateFilter: () => void;
    isLoadingMarketTrends: boolean;
    filteredMarketSalesTrendData: any[];
    marketSalesTrendData: any[];
    marketTrendsData: any;
    marketTrendsOptions: any;
    classes: any;
  };
  missingFieldsModalProps: {
    visible: boolean;
    onHide: () => void;
    report: any;
  };
  managerModalsProps: {
    isManagerReviewMode: boolean;
    bidId: string | undefined;
    requestModification: {
      visible: boolean;
      onHide: () => void;
      navigationItems: any[];
      bidId: number;
      onSuccess: () => void;
      bidStatus: string;
    };
    approve: {
      visible: boolean;
      onHide: () => void;
      bidId: number;
      onSuccess: () => void;
      bidStatus: string;
    };
  };
  requestNavigation: (path: string) => void;
  setShowMissingFieldsModal: (v: boolean) => void;
}

export function useMASDirectTemplateCore(): UseMASDirectTemplateCoreResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { bidId } = useParams<{ bidId: string }>();
  const {
    BidAnalysisStore,
    UserStore,
    NavbarStore: { setisNavigationRestricted },
  } = useStore();

  const {
    templateData,
    setTemplateData,
    bidOwner,
    setBidOwner,
    loading,
    setLoading,
    activeSection,
    setActiveSection,
    sidebarCollapsed,
    setSidebarCollapsed,
    activePackSizeTab,
    setActivePackSizeTab,
    activeYearTab,
    setActiveYearTab,
    selectedConcept,
    setSelectedConcept,
    expandedRows,
    setExpandedRows,
    pendingNavigation,
    setPendingNavigation,
    isBackNavigation,
    setIsBackNavigation,
    isNavigatingAway,
    setIsNavigatingAway,
    packSizeFormData,
    setPackSizeFormData,
    packSizeFormDataRef,
    yearFormData,
    setYearFormData,
    yearFormDataRef,
    copyAllYearsDataRef,
    expandedSections,
    setExpandedSections,
    pricingValidatedChecked,
    setPricingValidatedChecked,
    analysisType,
    setAnalysisType,
    gsmsHndlgType,
    setGsmsHndlgType,
    gsmsHndlgTypeManuallyChangedRef,
    formData,
    setFormData,
    selectedCompany,
    setSelectedCompany,
    validationErrors,
    setValidationErrors,
    ndcValidated,
    setNdcValidated,
    isValidatingNdc,
    setIsValidatingNdc,
    isLoadingNdcData,
    setIsLoadingNdcData,
    isSaving,
    setIsSaving,
    isSubmittingForReview,
    setIsSubmittingForReview,
    marketLandscapeData,
    setMarketLandscapeData,
    wasGsmsProfitManuallySet,
    setWasGsmsProfitManuallySet,
    showChartModal,
    setShowChartModal,
    showMissingFieldsModal,
    setShowMissingFieldsModal,
    missingFieldsReport,
    setMissingFieldsReport: _setMissingFieldsReport,
    isLoadingMarketTrends,
    setIsLoadingMarketTrends,
    isLoadingMarketShare,
    setIsLoadingMarketShare,
    volumeByCot,
    setVolumeByCot,
    isLoadingVolumeByCot,
    setIsLoadingVolumeByCot,
    volumeByCotAbortControllerRef,
    bidStatus,
    setBidStatus,
    selectedStatus,
    setSelectedStatus,
    isRequestModificationModalVisible,
    setIsRequestModificationModalVisible,
    isApproveDialogVisible,
    setIsApproveDialogVisible,
    notes,
    setNotes,
    isLoadingNotes: _isLoadingNotes,
    setIsLoadingNotes,
    notesPanelSection,
    setNotesPanelSection,
    notesPanelRef,
    ndcOptions,
    setNdcOptions,
    contractsList,
    setContractsList,
    handlingTypesList,
    setHandlingTypesList,
    supplierName,
    setSupplierName,
    supplierOptions,
    setSupplierOptions,
    marketSalesTrendData,
    setMarketSalesTrendData,
    filteredMarketSalesTrendData,
    setFilteredMarketSalesTrendData,
    setMarketShareByVolume,
    chartDateRange,
    setChartDateRange,
    tempDateRange,
    setTempDateRange,
    calendarViewDates,
    setCalendarViewDates,
    isApplyingFilterRef,
    marketSalesTrendAbortControllerRef,
    marketShareAbortControllerRef,
    contractPriceRankingAbortControllerRef,
    contractPriceRankingDebounceTimerRef,
    lastContractPriceRankingParamsRef,
    contractPriceRanking,
    setContractPriceRanking,
    isLoadingContractPriceRanking,
    setIsLoadingContractPriceRanking,
    sectionRefs,
    hasLoadedInitialData,
    contractsExplicitlyClearedRef,
    ndcNotFoundShownRef,
  } = useBidAnalysisState();

  const { recalculateFields } = useRecalculation({
    setWasGsmsProfitManuallySet,
    templateData,
    activeYearTab,
    contractPriceRanking,
    supplierName,
    analysisType,
  });

  const { loadNotes, loadTemplateData } = useBidDetailsLoad({
    setLoading,
    setTemplateData,
    setAnalysisType,
    setGsmsHndlgType,
    setPricingValidatedChecked,
    setFormData,
    setPackSizeFormData,
    setIsLoadingNotes,
    setNotes,
  });

  const {
    handleStatusChange,
    handleRequestModification,
    handleApprove,
    handleModificationSuccess,
    handleApproveSuccess,
  } = useSaveAndSubmit(
    {
      setSelectedStatus,
      setIsRequestModificationModalVisible,
      setIsApproveDialogVisible,
      setBidStatus,
    },
    loadNotes
  );

  const { toggleSection, scrollToSection } = useTemplateNavigation({
    setExpandedSections,
    setActiveSection,
    sectionRefs,
  });

  const preserveGlobalFields = useCallback(
    (newData: any): any => preserveGlobalFieldsLogic(newData, formData),
    [formData]
  );

  const handleExpandedRowsChange = useCallback(
    (newExpandedRows: any) => {
      let expandedRowsData = newExpandedRows;
      if (newExpandedRows && typeof newExpandedRows === 'object' && 'data' in newExpandedRows) {
        expandedRowsData = newExpandedRows.data;
        while (
          expandedRowsData &&
          typeof expandedRowsData === 'object' &&
          'data' in expandedRowsData &&
          Object.keys(expandedRowsData).length === 1
        ) {
          expandedRowsData = expandedRowsData.data;
        }
      }
      setExpandedRows(expandedRowsData || {});
    },
    [setExpandedRows]
  );

  const handleGsmsHndlgTypeChange = useCallback(
    (value: string) => {
      gsmsHndlgTypeManuallyChangedRef.current = true;
      setGsmsHndlgType(value);
    },
    [setGsmsHndlgType, gsmsHndlgTypeManuallyChangedRef]
  );

  const bidDetails = BidAnalysisStore.currentBidDetails;

  const validateField = useCallback(
    (fieldName: string, value: any): string | null =>
      validateFieldUtil(fieldName, value, {
        currentBidDetails: BidAnalysisStore.currentBidDetails,
        templateType: bidDetails?.template_type || 'mas',
        analysisType,
      }),
    [bidDetails?.template_type, analysisType, BidAnalysisStore.currentBidDetails]
  );

  const {
    loadContractsAndHandlingTypes,
    loadSuppliersForBid,
    getCurrentUser,
    handleNotesClick,
    loadNdcOptions,
    loadMarketSalesTrendData,
    setAndStoreChartDateRange: _setAndStoreChartDateRange,
    handleTempDateRangeChange,
    handleApplyDateFilter,
    handleDateRangeChange,
    loadMarketShareByVolume,
    loadVolumeByCot,
    loadContractPriceRanking,
  } = useDataLoaders({
    bidId,
    bidDetails,
    packSizeFormData,
    activePackSizeTab,
    formData,
    chartDateRange,
    tempDateRange,
    marketSalesTrendData,
    selectedCompany,
    setContractsList,
    setHandlingTypesList,
    setSupplierOptions,
    setSupplierName,
    setNotesPanelSection,
    setNdcOptions,
    setChartDateRange,
    setTempDateRange,
    setMarketSalesTrendData,
    setFilteredMarketSalesTrendData,
    setIsLoadingMarketTrends,
    setMarketShareByVolume,
    setIsLoadingMarketShare,
    setVolumeByCot,
    setIsLoadingVolumeByCot,
    setContractPriceRanking,
    setIsLoadingContractPriceRanking,
    notesPanelRef,
    ndcNotFoundShownRef,
    marketSalesTrendAbortControllerRef,
    marketShareAbortControllerRef,
    volumeByCotAbortControllerRef,
    contractPriceRankingAbortControllerRef,
    contractPriceRankingDebounceTimerRef,
    lastContractPriceRankingParamsRef: lastContractPriceRankingParamsRef as React.MutableRefObject<{
      startDate?: string;
      endDate?: string;
      concept?: string;
      packSize?: number;
    } | null>,
    isApplyingFilterRef,
    BidAnalysisStore,
    UserStore,
    supplierName,
    formatDateForChartAPI,
    applyDateFilter,
    transformMarketShareData,
  });

  const loadBidDetails = useCallback(() => {
    return loadBidDetailsImpl({
      bidId,
      setLoading,
      setBidStatus,
      setSelectedStatus,
      setBidOwner,
      gsmsHndlgTypeManuallyChangedRef,
      setGsmsHndlgType,
      setFormData,
      setTemplateData,
      setYearFormData,
      yearFormDataRef,
      setPackSizeFormData,
      setSelectedConcept,
      setActivePackSizeTab,
      setActiveYearTab,
      setAnalysisType,
      setPricingValidatedChecked,
      hasLoadedInitialData,
      setChartDateRange,
      setCalendarViewDates,
      setNdcValidated,
      BidAnalysisStore,
      supplierName,
      selectedConcept,
      loadContractsAndHandlingTypes,
      loadSuppliersForBid,
      loadNdcOptions,
    });
  }, [
    bidId,
    setLoading,
    setBidStatus,
    setSelectedStatus,
    setBidOwner,
    gsmsHndlgTypeManuallyChangedRef,
    setGsmsHndlgType,
    setFormData,
    setTemplateData,
    setYearFormData,
    yearFormDataRef,
    setPackSizeFormData,
    setSelectedConcept,
    setActivePackSizeTab,
    setActiveYearTab,
    setAnalysisType,
    setPricingValidatedChecked,
    hasLoadedInitialData,
    setChartDateRange,
    setCalendarViewDates,
    setNdcValidated,
    BidAnalysisStore,
    supplierName,
    selectedConcept,
    loadContractsAndHandlingTypes,
    loadSuppliersForBid,
    loadNdcOptions,
  ]);

  const isInitialLoadRef = useRef(true);
  const hasUserMadeChangesRef = useRef(false);
  const isUpdatingContractsRef = useRef(false);

  const userEmail = UserStore.userMetadata?.userInfo?.email || '';
  const reviewerEmail = bidDetails?.reviewer?.toLowerCase().trim() || '';
  const currentUserEmail = userEmail.toLowerCase().trim();
  const isManagerReviewMode = Boolean(bidDetails?.reviewer && reviewerEmail === currentUserEmail);

  const getDefaultViewDateWrapper = useCallback(
    (isStart: boolean = true): Date => {
      const currentData = packSizeFormData[activePackSizeTab] || formData;
      return getDefaultViewDate({
        isStart,
        currentData,
        bidDetails,
      });
    },
    [packSizeFormData, activePackSizeTab, formData, bidDetails]
  );

  const checkSectionCompletionWrapper = useCallback(
    (sectionId: string): 'completed' | 'warning' => {
      const currentBidDetails = BidAnalysisStore.currentBidDetails;
      const currentTemplateType = currentBidDetails?.template_type || 'mas';
      const currentData =
        currentTemplateType === 'nc' &&
        activePackSizeTab !== null &&
        activeYearTab !== null &&
        activeYearTab !== 5
          ? yearFormData[activePackSizeTab]?.[activeYearTab] || formData
          : packSizeFormData[activePackSizeTab] || formData;

      let rankingDataForCheck = contractPriceRanking;
      if (
        sectionId === 'contract-price-ranking' &&
        (!rankingDataForCheck || rankingDataForCheck.length === 0)
      ) {
        if (bidId && currentData.conceptName && currentData.packSize) {
          const bidIdNum = parseInt(bidId);
          const concept = currentData.conceptName;
          const packSize = parseFloat(currentData.packSize);
          const startDate = currentData.usagePeriodStartDate;
          const endDate = currentData.usagePeriodEndDate;
          const storeData = BidAnalysisStore.getContractPriceRankingData(
            bidIdNum,
            concept,
            packSize,
            startDate,
            endDate
          );
          if (storeData && storeData.length > 0) {
            rankingDataForCheck = storeData;
          }
        }
      }

      let ndcOptionsForCheck: Array<any> = [];
      if (
        sectionId === 'product-info' &&
        supplierName &&
        currentData.conceptName &&
        currentData.packSize
      ) {
        const concept = currentData.conceptName;
        const packSize = parseFloat(currentData.packSize);
        const storedNdcOptions = BidAnalysisStore.getNdcOptions(supplierName, concept, packSize);
        if (storedNdcOptions && storedNdcOptions.length > 0) {
          ndcOptionsForCheck = storedNdcOptions;
        }
      }

      return checkSectionCompletion({
        sectionId,
        currentData,
        formData,
        supplierName,
        marketSalesTrendData,
        contractPriceRanking: rankingDataForCheck || [],
        ndcOptions: ndcOptionsForCheck,
      });
    },
    [
      formData,
      packSizeFormData,
      yearFormData,
      activePackSizeTab,
      activeYearTab,
      supplierName,
      marketSalesTrendData,
      contractPriceRanking,
      bidId,
      BidAnalysisStore,
    ]
  );

  const {
    productName,
    productType,
    templateType,
    isIndirect: _isIndirect,
    navigationItems,
    concepts,
    packSizeTabs,
    allPackSizeTabs,
    yearTabs,
    formatTargetLaunchDateForDisplay,
    calculate5YearSummary,
    calculateProgressWrapper,
  } = useTemplateComputed({
    bidDetails,
    formData,
    selectedConcept,
    packSizeFormData,
    activePackSizeTab,
    ndcValidated,
    marketSalesTrendData,
    contractPriceRanking,
    checkSectionCompletionWrapper,
    copyAllYearsDataRef,
    yearFormDataRef,
    yearFormData,
    supplierName,
  });

  const { handleSaveBid } = useSaveBid({
    bidId,
    bidDetails,
    formData,
    yearFormDataRef,
    packSizeFormDataRef,
    packSizeTabs,
    gsmsHndlgType,
    analysisType,
    supplierName,
    selectedStatus,
    bidStatus,
    setBidStatus,
    setSelectedStatus,
    marketLandscapeData,
    calculateProgressWrapper,
    formatDateForAPI,
    setIsSaving,
    hasUserMadeChangesRef,
  });

  useCommonEffects({
    formData,
    setFormData,
    packSizeFormData,
    setPackSizeFormData,
    activePackSizeTab,
    activeYearTab,
    bidId,
    bidDetails,
    BidAnalysisStore,
    chartDateRange,
    setTempDateRange,
    tempDateRange,
    loadBidDetails,
    loadNotes,
    loadTemplateData,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    loadVolumeByCot,
    loadContractPriceRanking,
    setChartDateRange,
    setCalendarViewDates,
    recalculateFields,
    preserveGlobalFields,
    setValidationErrors,
    validateField,
    setNdcValidated,
    ndcValidated,
    setYearFormData,
    setGsmsHndlgType,
    gsmsHndlgTypeManuallyChangedRef,
    gsmsHndlgType,
    setNdcOptions,
    loadNdcOptions,
    selectedConcept,
    supplierName,
    handlingTypesList,
    contractsList,
    contractsExplicitlyClearedRef,
    setisNavigationRestricted,
    setIsNavigatingAway,
    location,
    isNavigatingAway,
    isManagerReviewMode,
    setPendingNavigation,
    setIsBackNavigation,
    isApplyingFilterRef,
    loading,
    hasUserMadeChangesRef,
    isInitialLoadRef,
    ndcNotFoundShownRef,
    yearFormData,
    yearFormDataRef,
    selectedCompany,
    analysisType,
    contractPriceRanking,
    showChartModal,
  });

  const getBackNavigationPath = useCallback(() => {
    if (isManagerReviewMode) {
      return '/bdportal/bid-analysis/manager-review';
    }
    return '/bdportal/bid-analysis/dashboard';
  }, [isManagerReviewMode]);

  const requestNavigation = useCallback(
    (path: string) => {
      setPendingNavigation(path);
    },
    [setPendingNavigation]
  );

  const handleSaveBidRef = useRef(handleSaveBid);
  useEffect(() => {
    handleSaveBidRef.current = handleSaveBid;
  }, [handleSaveBid]);

  const handleAutoSave = useCallback(async () => {
    if (!AUTO_SAVE_ENABLED) return;
    await handleSaveBidRef.current(true);
  }, []);

  const formDataWithoutContracts = useMemo(() => {
    const { contracts: _c, contractsData: _cd, ...rest } = formData;
    return rest;
  }, [formData]);

  useAutoSave(
    [
      formDataWithoutContracts,
      packSizeFormData,
      analysisType,
      supplierName,
      gsmsHndlgType,
      selectedStatus,
    ],
    {
      saveFn: handleAutoSave,
      delay: 3000,
      enabled: AUTO_SAVE_ENABLED && !!bidId && !loading,
      shouldSkip: () =>
        isSaving ||
        (isInitialLoadRef.current && !hasUserMadeChangesRef.current) ||
        !bidId ||
        isUpdatingContractsRef.current,
      onSaveSuccess: () => {},
      onSaveError: () => {},
    }
  );

  const handleSubmitForReview = useCallback(async () => {
    if (!bidId) {
      console.error('No bid ID available');
      toast.error('No bid ID available', { autoClose: 5000 });
      return;
    }
    setIsSubmittingForReview(true);
    try {
      await handleSaveBid();
      navigate(`/bdportal/bid-analysis/validate/${bidId}`);
    } catch (error: any) {
      console.error('Error saving bid before submit for review:', error);
    } finally {
      setIsSubmittingForReview(false);
    }
  }, [bidId, handleSaveBid, navigate, setIsSubmittingForReview]);

  const { handleFieldChange } = useFieldHandlers({
    bidDetails,
    templateType,
    activePackSizeTab,
    activeYearTab,
    yearFormData,
    formData,
    setYearFormData,
    yearFormDataRef,
    setPackSizeFormData,
    setFormData,
    setValidationErrors,
    hasUserMadeChangesRef,
    isInitialLoadRef,
    validateField,
  });

  useMASDirectEffects({
    BidAnalysisStore,
    gsmsHndlgType,
    activePackSizeTab,
    activeYearTab,
    packSizeFormData,
    yearFormData,
    formData,
    recalculateFields,
    setPackSizeFormData,
    setFormData,
    setYearFormData,
  });

  const {
    handleFieldBlur,
    handleFieldKeyDown,
    replicateAcrossPackSizes,
    handlePackSizeTabChange,
    handleYearTabChange,
    copyForAllYears,
    handleMarketShareClick,
  } = useBidHandlers({
    formData,
    yearFormData,
    packSizeFormData,
    activePackSizeTab,
    activeYearTab,
    bidDetails,
    templateType,
    productType,
    supplierName,
    contractsList,
    contractPriceRanking,
    ndcValidated,
    selectedConcept,
    analysisType,
    gsmsHndlgType,
    allPackSizeTabs,
    packSizeTabs,
    templateData,
    chartDateRange,
    selectedCompany,
    bidId,
    yearFormDataRef,
    isInitialLoadRef,
    hasUserMadeChangesRef,
    contractPriceRankingAbortControllerRef,
    marketSalesTrendAbortControllerRef,
    copyAllYearsDataRef,
    setFormData,
    setYearFormData,
    setPackSizeFormData,
    setValidationErrors,
    setNdcValidated,
    setIsValidatingNdc,
    setIsLoadingNdcData,
    setNdcOptions,
    setActivePackSizeTab,
    setActiveYearTab,
    setSelectedCompany,
    recalculateFields,
    loadContractPriceRanking,
    loadNdcOptions,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    loadVolumeByCot,
    BidAnalysisStore,
    validateField,
    formatTargetLaunchDateForDisplay,
    setChartDateRange,
    setCalendarViewDates,
    handleFieldChange,
    wasGsmsProfitManuallySet,
    setWasGsmsProfitManuallySet,
    packSizeFormDataRef,
    setContractPriceRanking,
    setFilteredMarketSalesTrendData,
    setMarketSalesTrendData,
    setMarketShareByVolume,
  });

  const marketTrendsData = getMarketTrendsChartData(
    marketSalesTrendData,
    filteredMarketSalesTrendData,
    chartDateRange
  );
  const marketTrendsOptions = useMarketTrendsChartOptions();

  const baseCtxForMas = {
    sectionRefs,
    expandedSections,
    toggleSection,
    templateData,
    formData,
    yearFormData,
    activePackSizeTab,
    activeYearTab,
    setActivePackSizeTab,
    setActiveYearTab,
    analysisType,
    gsmsHndlgType,
    supplierName,
    supplierOptions,
    contractsList,
    handlingTypesList,
    pricingValidatedChecked,
    validationErrors,
    handleFieldChange,
    handleFieldBlur,
    handleFieldKeyDown,
    setAnalysisType,
    handleGsmsHndlgTypeChange,
    setSupplierName,
    isSaving,
    setYearFormData,
    setFormData,
    setPackSizeFormData,
    notes,
    handleNotesClick,
    selectedConcept,
    setSelectedConcept,
    concepts,
    packSizeTabs,
    handlePackSizeTabChange,
    handleYearTabChange,
    SECTION_NAMES,
    SECTION_DISPLAY_NAMES,
    bidDetails,
    templateType,
    productType,
    bidId,
    allPackSizeTabs,
    yearTabs,
    packSizeFormData,
    yearFormDataRef,
    ndcOptions,
    ndcValidated,
    setNdcValidated,
    isLoadingNdcData,
    setIsLoadingNdcData,
    isValidatingNdc,
    setIsValidatingNdc,
    copyForAllYears,
    calculate5YearSummary,
    marketLandscapeData,
    setMarketLandscapeData,
    chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    isLoadingMarketTrends,
    isLoadingMarketShare,
    volumeByCot,
    isLoadingVolumeByCot,
    loadVolumeByCot,
    marketTrendsOptions,
    handleDateRangeChange,
    handleApplyDateFilter,
    setCalendarViewDates,
    handleMarketShareClick,
    setShowChartModal,
    contractPriceRanking,
    expandedRows,
    handleExpandedRowsChange,
    isLoadingContractPriceRanking,
    loadNotes,
    loadContractPriceRanking,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    formatTargetLaunchDateForDisplay,
    getCurrentUser,
    bidOwner,
    productName,
    setChartDateRange,
    handleTempDateRangeChange,
    ndcNotFoundShownRef,
    hasLoadedInitialData,
    recalculateFields,
  };

  const masCtx: MASTemplateContentCtx = {
    ...baseCtxForMas,
    isDirect: true,
    isIndirect: false,
    isUpdatingContractsRef,
    contractsExplicitlyClearedRef,
    setPackSizeFormData,
    setContractPriceRanking,
    replicateAcrossPackSizes,
  };

  return {
    loading,
    bidDetails,
    templateType,
    productType,
    templateData,
    masCtx,
    layoutProps: {
      pendingNavigation,
      setPendingNavigation,
      isBackNavigation,
      setIsBackNavigation,
      setIsNavigatingAway,
      isSaving,
      navigate,
      getBackNavigationPath,
      handleSaveBid,
      productName,
      isSubmittingForReview,
      progress: calculateProgressWrapper(),
      selectedStatus,
      bidStatus: bidDetails?.status || selectedStatus,
      onSave: () => handleSaveBid().then(r => r as true | undefined),
      onSubmit: handleSubmitForReview,
      onStatusChange: handleStatusChange,
      isManagerReviewMode,
      onRequestModification: handleRequestModification,
      onApprove: handleApprove,
      missingFieldsReport,
      onShowMissingFields: () => setShowMissingFieldsModal(true),
      templateType,
      onRequestNavigation: requestNavigation,
      sidebarCollapsed,
      onToggleCollapse: () => setSidebarCollapsed(!sidebarCollapsed),
      navigationItems,
      activeSection,
      onSectionClick: scrollToSection,
      productType: productType as 'direct' | 'indirect' | null,
    },
    notesPanelProps: {
      ref: notesPanelRef,
      bidId: parseInt(bidId || '0'),
      sectionName: notesPanelSection,
      notes,
      onNotesChange: loadNotes,
      currentUser: getCurrentUser(),
      readOnly: notesPanelSection === 'manager_modification',
    },
    chartModalProps: {
      visible: showChartModal,
      onHide: () => setShowChartModal(false),
      selectedCompany,
      tempDateRange,
      calendarViewDates,
      getDefaultViewDate: getDefaultViewDateWrapper,
      handleTempDateRangeChange,
      setCalendarViewDates,
      handleApplyDateFilter,
      isLoadingMarketTrends,
      filteredMarketSalesTrendData,
      marketSalesTrendData,
      marketTrendsData,
      marketTrendsOptions,
      classes,
    },
    missingFieldsModalProps: {
      visible: showMissingFieldsModal,
      onHide: () => setShowMissingFieldsModal(false),
      report: missingFieldsReport,
    },
    managerModalsProps: {
      isManagerReviewMode,
      bidId,
      requestModification: {
        visible: isRequestModificationModalVisible,
        onHide: () => setIsRequestModificationModalVisible(false),
        navigationItems,
        bidId: parseInt(bidId || '0'),
        onSuccess: handleModificationSuccess,
        bidStatus: bidDetails?.status || selectedStatus,
      },
      approve: {
        visible: isApproveDialogVisible,
        onHide: () => setIsApproveDialogVisible(false),
        bidId: parseInt(bidId || '0'),
        onSuccess: handleApproveSuccess,
        bidStatus: bidDetails?.status || selectedStatus,
      },
    },
    requestNavigation,
    setShowMissingFieldsModal,
  };
}
